import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  Users, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Car,
  AlertCircle,
  Users2,
  Route,
  Clock,
  DollarSign,
  Target,
  Zap,
  MapPin,
  Star,
  CheckCircle,
  XCircle,
  Timer
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-500" size={20} />
          <h3 className="text-lg font-semibold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <TrendingUp className="text-blue-500" size={20} /> : <TrendingDown className="text-blue-500" size={20} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-800 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const PrivateRideAnalyticsReport = () => {
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("totalRides");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    driverAnalytics: true,
    customerAnalytics: true,
    routeAnalytics: true,
    performanceMetrics: true
  });

  // Data states
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Fetch private ride data
  const fetchPrivateRideData = async () => {
    setLoading(true);
    try {
      // Fetch private ride data from backend
      const [tripsRes, driversRes, customersRes] = await Promise.all([
        fetch(`${API_URL}/trip/`),                        // All private trips
        fetch(`${API_URL}/driver/`),                      // Drivers
        fetch(`${API_URL}/customerdetails/`)               // Customers
      ]);

      const [tripsData, driversData, customersData] = await Promise.all([
        tripsRes.json(),
        driversRes.json(),
        customersRes.json()
      ]);

      console.log("Private trips data:", tripsData);
      console.log("Drivers data:", driversData);
      console.log("Customers data:", customersData);

      setTrips(Array.isArray(tripsData) ? tripsData : []);
      setDrivers(Array.isArray(driversData) ? driversData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error("Error fetching private ride data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrivateRideData();
    const interval = setInterval(fetchPrivateRideData, 30000);
    return () => clearInterval(interval);
  }, [timeFilter]);

  // Calculate private ride analytics
  const privateRideAnalytics = useMemo(() => {
    if (!Array.isArray(trips) || !trips.length) {
      return {
        totalTrips: 0,
        completedTrips: 0,
        activeTrips: 0,
        cancelledTrips: 0,
        pendingTrips: 0,
        counterOffers: 0,
        waitingPayment: 0,
        totalRevenue: 0,
        averageRevenue: 0,
        driverParticipation: 0,
        customerParticipation: 0,
        completionRate: 0,
        averageRating: 0
      };
    }

    const totalTrips = trips.length;
    const completedTrips = trips.filter(trip => trip.status === 'Completed' || trip.status === 'Trip Ended' || trip.status === 'TripEnded').length;
    const activeTrips = trips.filter(trip => trip.status === 'In-Transit' || trip.status === 'InTransit').length;
    const cancelledTrips = trips.filter(trip => trip.status === 'Cancelled').length;
    const pendingTrips = trips.filter(trip => trip.status === 'New Order' || trip.status === 'Just In' || trip.status === 'Pending').length;
    const counterOffers = trips.filter(trip => trip.status === 'Counter Offer').length;
    const waitingPayment = trips.filter(trip => trip.status === 'Waiting Payment').length;
    
    const totalRevenue = trips.reduce((sum, trip) => sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0);
    const averageRevenue = totalTrips > 0 ? totalRevenue / totalTrips : 0;
    
    const driverParticipation = drivers.length > 0 ? (trips.filter(trip => trip.driver_id && trip.driver_id !== '0').length / drivers.length) * 100 : 0;
    const customerParticipation = customers.length > 0 ? (trips.length / customers.length) * 100 : 0;
    const completionRate = totalTrips > 0 ? (completedTrips / totalTrips) * 100 : 0;
    
    // Calculate average rating from completed trips with feedback
    const tripsWithRating = trips.filter(trip => trip.driver_stars > 0);
    const averageRating = tripsWithRating.length > 0 
      ? tripsWithRating.reduce((sum, trip) => sum + (trip.driver_stars || 0), 0) / tripsWithRating.length 
      : 0;

    return {
      totalTrips,
      completedTrips,
      activeTrips,
      cancelledTrips,
      pendingTrips,
      counterOffers,
      waitingPayment,
      totalRevenue,
      averageRevenue,
      driverParticipation,
      customerParticipation,
      completionRate,
      averageRating
    };
  }, [trips, drivers, customers]);

  // Filter and sort data
  const filteredTrips = useMemo(() => {
    if (!Array.isArray(trips) || !Array.isArray(trips)) return [];
    
    return trips.filter(trip => 
      (trip.origin_location && trip.origin_location.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (trip.destination_location && trip.destination_location.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (trip.driver_id && trip.driver_id.toString().includes(searchTerm.toLowerCase()))
    );
  }, [trips, searchTerm]);

  const sortedTrips = useMemo(() => {
    if (!Array.isArray(filteredTrips)) return [];
    
    return [...filteredTrips].sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'revenue':
          aValue = parseFloat(a.delivery_cost_proposed) || 0;
          bValue = parseFloat(b.delivery_cost_proposed) || 0;
          break;
        case 'date':
          aValue = new Date(a.request_start_datetime || 0);
          bValue = new Date(b.request_start_datetime || 0);
          break;
        default:
          aValue = parseFloat(a.delivery_cost_proposed) || 0;
          bValue = parseFloat(b.delivery_cost_proposed) || 0;
      }
      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    });
  }, [filteredTrips, sortBy, sortOrder]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const exportData = (data, filename) => {
    if (!Array.isArray(data) || data.length === 0) return;
    
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
            <Car className="mr-3 text-green-600" />
            Private Ride Analytics Report
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Comprehensive analysis of private ride operations, driver performance, and customer satisfaction
          </p>
        </div>

        {/* Report Tooltip */}
        <ReportTooltip title="Understanding Private Ride Analytics">
          <p><strong>Key Metrics:</strong></p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Total Trips:</strong> Number of private ride requests created</li>
            <li><strong>Completion Rate:</strong> Percentage of rides that were successfully completed</li>
            <li><strong>Average Revenue:</strong> Average earnings per completed ride</li>
            <li><strong>Driver Participation:</strong> Ratio of drivers actively accepting rides</li>
            <li><strong>Customer Participation:</strong> Ratio of customers using private rides</li>
            <li><strong>Average Rating:</strong> Customer satisfaction scores from completed rides</li>
          </ul>
          <p className="mt-2"><strong>Business Value:</strong></p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Optimize pricing strategies based on completion rates</li>
            <li>Identify high-performing drivers and routes</li>
            <li>Improve customer satisfaction through rating analysis</li>
            <li>Maximize revenue through better driver utilization</li>
            <li>Reduce cancellation rates through better matching</li>
          </ul>
        </ReportTooltip>

        {/* Time Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center space-x-2 mb-2 sm:mb-0">
              <Calendar className="text-green-600" size={20} />
              <span className="font-medium text-gray-700 dark:text-gray-300">Time Period:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "all", label: "All Time" },
                { value: "today", label: "Today" },
                { value: "7days", label: "Past 7 Days" },
                { value: "30days", label: "Past 30 Days" }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setTimeFilter(filter.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    timeFilter === filter.value
                      ? "bg-green-600 text-white shadow-lg transform scale-105"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Private Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {privateRideAnalytics.totalTrips}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {privateRideAnalytics.activeTrips} active
                </p>
              </div>
              <Route className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Completion Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {privateRideAnalytics.completionRate.toFixed(1)}%
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {privateRideAnalytics.completedTrips} completed
                </p>
              </div>
              <Target className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${privateRideAnalytics.totalRevenue.toFixed(2)}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  Avg: ${privateRideAnalytics.averageRevenue.toFixed(2)}/ride
                </p>
              </div>
              <DollarSign className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Average Rating</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {privateRideAnalytics.averageRating.toFixed(1)}
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400">
                  Customer satisfaction
                </p>
              </div>
              <Star className="text-yellow-500" size={24} />
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Activity className="mr-2 text-green-600" />
            Trip Status Breakdown
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 dark:bg-green-900 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {privateRideAnalytics.completedTrips}
              </div>
              <div className="text-sm text-green-800 dark:text-green-200">Completed + Trip Ended</div>
            </div>
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {privateRideAnalytics.activeTrips}
              </div>
              <div className="text-sm text-blue-800 dark:text-blue-200">In-Transit</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {privateRideAnalytics.pendingTrips}
              </div>
              <div className="text-sm text-yellow-800 dark:text-yellow-200">New Order + Just In</div>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900 rounded-lg">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {privateRideAnalytics.waitingPayment}
              </div>
              <div className="text-sm text-orange-800 dark:text-orange-200">Waiting Payment</div>
            </div>
            <div className="text-center p-4 bg-red-50 dark:bg-red-900 rounded-lg">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {privateRideAnalytics.cancelledTrips}
              </div>
              <div className="text-sm text-red-800 dark:text-red-200">Cancelled</div>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-900 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {privateRideAnalytics.counterOffers}
              </div>
              <div className="text-sm text-purple-800 dark:text-purple-200">Counter Offer</div>
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Debug Information</h3>
          <div className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
            <p>Private trips data: {Array.isArray(trips) ? `Array(${trips.length})` : 'Not an array'}</p>
            <p>Drivers data: {Array.isArray(drivers) ? `Array(${drivers.length})` : 'Not an array'}</p>
            <p>Customers data: {Array.isArray(customers) ? `Array(${customers.length})` : 'Not an array'}</p>
            {trips.length > 0 && (
              <>
                <p>Sample trip: {JSON.stringify(trips[0], null, 2).substring(0, 200)}...</p>
                <p>Status values found: {[...new Set(trips.map(trip => trip.status).filter(Boolean))].join(', ')}</p>
                <p>Status counts: {JSON.stringify([...new Set(trips.map(trip => trip.status).filter(Boolean))].reduce((acc, status) => {
                  acc[status] = trips.filter(trip => trip.status === status).length;
                  return acc;
                }, {}), null, 2)}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PrivateRideAnalyticsReport;
